import { useCallback, useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/cn";

export interface Series {
  label: string;
  /** One value per point, in the same order as `labels`. */
  values: number[];
  /** Solid line only, or a line over a fading wash. */
  fill?: boolean;
  colour: "brand" | "cyan" | "iris" | "go" | "ochre" | "ink";
}

export interface AreaChartProps {
  labels: string[];
  series: Series[];
  /** Drawn behind everything as a dashed rule, e.g. what a branch asked for. */
  marker?: { value: number; label: string };
  height?: number;
  /**
   * A scale down the left and a rule at each step of it, so a reader can
   * judge a height rather than only a shape. Off by default: on a small
   * trend they are clutter, and on a full-size chart of figures they are
   * the difference between a picture and a reading.
   */
  gridlines?: boolean;
  /** How a value is written in the readout, e.g. `(v) => `${v} h``. */
  format?: (value: number) => string;
  className?: string;
}

export interface SparklineProps {
  values: number[];
  colour?: Series["colour"];
  /** Accessible name. A sparkline with no label is decoration. */
  label: string;
  /** How the hovered value is written. Defaults to at most one decimal. */
  format?: (value: number) => string;
  className?: string;
}

/** At most one decimal, and none at all when the value is a whole number. */
function readable(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

const STROKE = {
  brand: "var(--color-brand-500)",
  cyan: "var(--color-cyan-500)",
  iris: "var(--color-iris-500)",
  go: "var(--color-go-500)",
  ochre: "var(--color-ochre-500)",
  ink: "var(--color-ink-400)",
} as const;

type Point = readonly [number, number];

/**
 * A smooth line through the points that never leaves the range between two
 * neighbours.
 *
 * Monotone cubic interpolation, the Fritsch–Carlson kind. It replaced a
 * Catmull-Rom spline, which is smoother and tells small lies: between a flat
 * run and a sharp rise it dips first, so a rota that never dropped below
 * four showed a day at three and a half, and a week of no overtime showed
 * less than none. This one flattens at a peak instead of overshooting it.
 */
function curve(points: readonly Point[]): string {
  const count = points.length;
  if (count === 0) return "";
  if (count < 3) return points.map(([x, y], i) => `${i ? "L" : "M"}${x},${y}`).join(" ");

  const run: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < count - 1; i += 1) {
    run.push(points[i + 1]![0] - points[i]![0]);
    slope.push((points[i + 1]![1] - points[i]![1]) / (run[i] || 1));
  }
  const tangent: number[] = [slope[0]!];
  for (let i = 1; i < count - 1; i += 1) {
    // A turning point is flat, which is what stops the overshoot.
    tangent.push(slope[i - 1]! * slope[i]! <= 0 ? 0 : (slope[i - 1]! + slope[i]!) / 2);
  }
  tangent.push(slope[count - 2]!);
  for (let i = 0; i < count - 1; i += 1) {
    if (slope[i] === 0) {
      tangent[i] = 0;
      tangent[i + 1] = 0;
      continue;
    }
    const a = tangent[i]! / slope[i]!;
    const b = tangent[i + 1]! / slope[i]!;
    const length = Math.hypot(a, b);
    if (length > 3) {
      tangent[i] = (3 / length) * a * slope[i]!;
      tangent[i + 1] = (3 / length) * b * slope[i]!;
    }
  }

  let d = `M${points[0]![0]},${points[0]![1]}`;
  for (let i = 0; i < count - 1; i += 1) {
    const [x1, y1] = points[i]!;
    const [x2, y2] = points[i + 1]!;
    const third = run[i]! / 3;
    d += ` C${x1 + third},${y1 + tangent[i]! * third} ${x2 - third},${y2 - tangent[i + 1]! * third} ${x2},${y2}`;
  }
  return d;
}

/**
 * The steps of the scale: round numbers, about four of them, the top one at
 * or above the largest value so no line is drawn against the chart's edge.
 */
function scale(max: number, whole: boolean): number[] {
  const rough = Math.max(max, whole ? 1 : 0.1) / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const unit = rough / power;
  let step = (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10) * power;
  // Half a member of staff is not a step anybody wants to read.
  if (whole) step = Math.max(1, Math.round(step));
  const steps: number[] = [];
  for (let value = 0; value < max + step - 1e-9; value += step) {
    steps.push(Number(value.toFixed(6)));
  }
  return steps.length > 1 ? steps : [0, step];
}

/** An id safe to put inside url(#…): a label with spaces in it silently fails. */
function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

/**
 * An area chart, drawn by hand.
 *
 * No charting library: the one thing this has to do is draw two dozen points
 * as a line with a scale beside it, and pulling in three hundred kilobytes to
 * do it would cost more than it is worth.
 *
 * It is drawn at the size it is shown. The first one was drawn in a fixed
 * 720-unit box and stretched to fit, which is why its line was thicker
 * across than down, its dots were ellipses, and its labels had to live in a
 * list underneath that never quite lined up with the points. This one
 * measures its own width, so a pixel is a pixel: the line is one weight,
 * the dates sit under their own points, and the scale is real text.
 */
export function AreaChart({
  labels,
  series,
  marker,
  height = 200,
  gridlines = false,
  format = readable,
  className,
}: AreaChartProps) {
  const id = useId().replace(/:/g, "");
  const frame = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [at, setAt] = useState<number | null>(null);

  useEffect(() => {
    const node = frame.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const everything = [...series.flatMap((one) => one.values), marker ? marker.value : 0];
  const steps = scale(Math.max(0, ...everything), everything.every(Number.isInteger));
  const top = steps[steps.length - 1]!;

  const left = gridlines ? 12 + Math.max(...steps.map((one) => readable(one).length)) * 7 : 8;
  const right = 12;
  const above = 10;
  const below = 26;
  const innerWidth = Math.max(1, width - left - right);
  const innerHeight = Math.max(1, height - above - below);
  const count = labels.length;
  const gap = count > 1 ? innerWidth / (count - 1) : 0;
  const x = useCallback(
    (index: number) => (count > 1 ? left + index * gap : left + innerWidth / 2),
    [count, left, gap, innerWidth],
  );
  const y = (value: number) => above + innerHeight * (1 - value / top);
  const floor = y(0);

  // As many dates as fit without touching, evenly spaced from the first.
  const stride = Math.max(1, Math.ceil(count / Math.max(2, Math.floor(innerWidth / 64))));

  const track = useCallback(
    (clientX: number) => {
      const box = frame.current?.getBoundingClientRect();
      if (!box || box.width === 0 || count === 0) return;
      const inChart = ((clientX - box.left) / box.width) * width;
      const index = gap > 0 ? Math.round((inChart - left) / gap) : 0;
      setAt(Math.min(count - 1, Math.max(0, index)));
    },
    [count, gap, left, width],
  );

  function onKeyDown(event: React.KeyboardEvent) {
    const last = count - 1;
    const move =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (move !== 0) {
      setAt((current) => Math.min(last, Math.max(0, (current ?? last) + move)));
    } else if (event.key === "Home") {
      setAt(0);
    } else if (event.key === "End") {
      setAt(last);
    } else if (event.key === "Escape") {
      setAt(null);
    } else {
      return;
    }
    event.preventDefault();
  }

  const reading =
    at === null
      ? "Use the arrow keys to read the values."
      : `${labels[at]}: ${series.map((one) => `${one.label} ${format(one.values[at] ?? 0)}`).join(", ")}`;

  return (
    <div className={cn("w-full", className)}>
      {/* Focusable, because a readout only a mouse can reach is half a
          feature. Arrow keys walk the points; Escape puts it away. */}
      <div
        ref={frame}
        tabIndex={0}
        role="img"
        aria-label={`${series.map((one) => one.label).join(", ")}. ${reading}`}
        onPointerMove={(event) => track(event.clientX)}
        onPointerDown={(event) => track(event.clientX)}
        onPointerLeave={() => setAt(null)}
        onFocus={() => setAt((current) => current ?? count - 1)}
        onBlur={() => setAt(null)}
        onKeyDown={onKeyDown}
        className="relative rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          preserveAspectRatio="none"
          className="block"
          aria-hidden="true"
        >
          <defs>
            {series.map((one) => (
              <linearGradient
                key={one.label}
                id={`${id}-${slug(one.label)}`}
                gradientUnits="userSpaceOnUse"
                x1="0"
                x2="0"
                y1={above}
                y2={floor}
              >
                <stop offset="0%" stopColor={STROKE[one.colour]} stopOpacity="0.2" />
                <stop offset="100%" stopColor={STROKE[one.colour]} stopOpacity="0.02" />
              </linearGradient>
            ))}
          </defs>

          {/* The scale: a rule at each step and its figure beside it. */}
          {gridlines
            ? steps.map((step) => (
                <g key={step}>
                  {step > 0 ? (
                    <line
                      x1={left}
                      x2={width - right}
                      y1={y(step)}
                      y2={y(step)}
                      stroke="var(--color-line-faint)"
                      strokeWidth="1"
                      shapeRendering="crispEdges"
                    />
                  ) : null}
                  <text
                    x={left - 8}
                    y={y(step)}
                    dy="0.32em"
                    textAnchor="end"
                    fontSize="12"
                    fill="var(--color-fg-subtle)"
                  >
                    {readable(step)}
                  </text>
                </g>
              ))
            : null}

          {/* The floor the areas stand on. */}
          <line
            x1={left}
            x2={width - right}
            y1={floor}
            y2={floor}
            stroke="var(--color-line-strong)"
            strokeWidth="1"
            shapeRendering="crispEdges"
          />

          {marker ? (
            <line
              x1={left}
              x2={width - right}
              y1={y(marker.value)}
              y2={y(marker.value)}
              stroke="var(--color-ink-500)"
              strokeDasharray="4 4"
              strokeWidth="1"
            />
          ) : null}

          {series.map((one) => {
            const points = one.values.map((value, index) => [x(index), y(value)] as const);
            const d = curve(points);
            const last = points[points.length - 1];
            return (
              <g key={one.label}>
                {one.fill && points.length > 1 ? (
                  <path
                    d={`${d} L${last![0]},${floor} L${points[0]![0]},${floor} Z`}
                    fill={`url(#${id}-${slug(one.label)})`}
                  />
                ) : null}
                <path
                  d={d}
                  fill="none"
                  stroke={STROKE[one.colour]}
                  strokeWidth={one.fill ? 2 : 1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Where the line has got to: the last point, marked. */}
                {one.fill && last && at === null ? (
                  <circle
                    cx={last[0]}
                    cy={last[1]}
                    r="3.5"
                    fill={STROKE[one.colour]}
                    stroke="var(--color-surface)"
                    strokeWidth="2"
                  />
                ) : null}
              </g>
            );
          })}

          {at !== null ? (
            <g>
              <line
                x1={x(at)}
                x2={x(at)}
                y1={above}
                y2={floor}
                stroke="var(--color-ink-400)"
                strokeWidth="1"
                shapeRendering="crispEdges"
              />
              {series.map((one) => (
                <circle
                  key={one.label}
                  cx={x(at)}
                  cy={y(one.values[at] ?? 0)}
                  r="4"
                  fill="var(--color-surface)"
                  stroke={STROKE[one.colour]}
                  strokeWidth="2"
                />
              ))}
            </g>
          ) : null}

          {/* The dates, each under its own point. */}
          {labels.map((label, index) =>
            index % stride === 0 || index === at ? (
              // While a point is being read, its date replaces its
              // neighbours' rather than printing over them.
              at !== null && index !== at && Math.abs(index - at) < stride ? null : (
                <text
                  key={label + index}
                  x={x(index)}
                  y={height - 7}
                  textAnchor={
                    count > 1 && index === count - 1 ? "end" : index === 0 && !gridlines ? "start" : "middle"
                  }
                  fontSize="12"
                  fontWeight={index === at ? 600 : 400}
                  fill={index === at ? "var(--color-fg)" : "var(--color-fg-subtle)"}
                >
                  {label}
                </text>
              )
            ) : null,
          )}
        </svg>

        {at !== null ? (
          <div
            className={cn(
              "pointer-events-none absolute top-0 z-10 w-max max-w-56 rounded-sm bg-surface-raised px-2.5 py-1.5 shadow-raised ring-1 ring-line",
              // Near an edge the bubble stops following the line and tucks
              // against the side, rather than hanging off the card.
              at <= 1
                ? "translate-x-2"
                : at >= count - 2
                  ? "-translate-x-[calc(100%+0.5rem)]"
                  : "-translate-x-1/2",
            )}
            style={{ left: `${(x(at) / width) * 100}%` }}
          >
            <p className="text-2xs font-medium text-fg-subtle">{labels[at]}</p>
            <ul className="mt-0.5 space-y-0.5">
              {series.map((one) => (
                <li key={one.label} className="flex items-center gap-2 whitespace-nowrap text-xs">
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: STROKE[one.colour] }}
                    aria-hidden="true"
                  />
                  <span className="text-fg-muted">{one.label}</span>
                  <span className="ml-auto pl-3 font-semibold tabular-nums text-fg">
                    {format(one.values[at] ?? 0)}
                  </span>
                </li>
              ))}
              {marker ? (
                <li className="flex items-center gap-2 whitespace-nowrap text-xs">
                  <span
                    className="h-0 w-2 shrink-0 border-t border-dashed border-ink-500"
                    aria-hidden="true"
                  />
                  <span className="text-fg-muted">{marker.label}</span>
                  <span className="ml-auto pl-3 font-semibold tabular-nums text-fg">
                    {format(marker.value)}
                  </span>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A legend.
 *
 * Each swatch is drawn the way its line is drawn: a dot for a series, a
 * dashed rule for a marker. A key that shows a solid block for a dashed line
 * is a key the reader has to decode, which is the one job it had.
 */
export function ChartKey({
  series,
  marker,
}: {
  series: Series[];
  /** The dashed rule, if the chart has one. Same object as AreaChart's. */
  marker?: { label: string };
}) {
  return (
    <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
      {series.map((one) => (
        <li key={one.label} className="flex items-center gap-2 text-xs text-fg-muted">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: STROKE[one.colour] }}
            aria-hidden="true"
          />
          {one.label}
        </li>
      ))}
      {marker ? (
        <li className="flex items-center gap-2 text-xs text-fg-muted">
          <span
            className="h-0 w-4 shrink-0 border-t border-dashed border-ink-500"
            aria-hidden="true"
          />
          {marker.label}
        </li>
      ) : null}
    </ul>
  );
}

/**
 * A figure's trend, drawn small enough to sit beside it.
 *
 * No axis, no labels, no marker — a sparkline answers "which way" and nothing
 * else. The moment it needs a scale it wants to be an AreaChart in a card of
 * its own. Kept in this file because it draws the same curve, and splitting
 * them would mean maintaining it twice.
 */
export function Sparkline({
  values,
  colour = "brand",
  label,
  format = readable,
  className,
}: SparklineProps) {
  const id = useId().replace(/:/g, "");
  const frame = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<number | null>(null);
  const width = 120;
  const height = 32;
  const pad = 3;
  const max = Math.max(1, ...values);

  if (values.length < 2) return null;

  const step = (width - pad * 2) / Math.max(1, values.length - 1);
  const y = (value: number) => height - pad - (value / max) * (height - pad * 2);
  const d = curve(values.map((value, index) => [pad + index * step, y(value)] as const));

  function track(clientX: number) {
    const box = frame.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    const inChart = ((clientX - box.left) / box.width) * width;
    const index = Math.round((inChart - pad) / step);
    setAt(Math.min(values.length - 1, Math.max(0, index)));
  }

  return (
    // Hover reads a value off it. No focus handling and no keyboard walk: a
    // sparkline is the same figures the tile already states, and a second tab
    // stop on every tile would cost a keyboard user more than it gives them.
    <div
      ref={frame}
      className={cn("relative", className)}
      onPointerMove={(event) => track(event.clientX)}
      onPointerDown={(event) => track(event.clientX)}
      onPointerLeave={() => setAt(null)}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-8 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={label}
      >
        <defs>
          <linearGradient id={`spark-${id}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={STROKE[colour]} stopOpacity="0.22" />
            <stop offset="100%" stopColor={STROKE[colour]} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d={`${d} L${width - pad},${height - pad} L${pad},${height - pad} Z`}
          fill={`url(#spark-${id})`}
        />
        <path
          d={d}
          fill="none"
          stroke={STROKE[colour]}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          // The svg is stretched to its box; this keeps the line one weight.
          vectorEffect="non-scaling-stroke"
        />
        {at !== null ? (
          <circle
            cx={pad + at * step}
            cy={y(values[at] ?? 0)}
            r="3"
            fill="var(--color-surface)"
            stroke={STROKE[colour]}
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
            aria-hidden="true"
          />
        ) : null}
      </svg>

      {at !== null ? (
        <span
          className={cn(
            "pointer-events-none absolute bottom-full z-10 mb-1 rounded-xs bg-surface px-1.5 py-0.5",
            "text-2xs font-semibold tabular-nums text-fg shadow-pop ring-1 ring-inset ring-line",
            at <= 1
              ? "translate-x-0"
              : at >= values.length - 2
                ? "-translate-x-full"
                : "-translate-x-1/2",
          )}
          style={{ left: `${((pad + at * step) / width) * 100}%` }}
        >
          {format(values[at] ?? 0)}
        </span>
      ) : null}
    </div>
  );
}
